import { useCallback, useSyncExternalStore } from 'react';

/* Tema V2: los valores viven en packages/tokens/tokens.css —
 * :root = oscuro, :root[data-theme="light"] = claro (traducción de
 * lightVars() del handoff). Acá solo se administra el atributo y su
 * persistencia. Sin atributo = oscuro. */

export type Theme = 'dark' | 'light';

const STORAGE_KEY = 'evo-theme';

function readTheme(): Theme {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'light' ? 'light' : 'dark';
  } catch {
    return 'dark';
  }
}

let current: Theme = readTheme();
const listeners = new Set<() => void>();

function apply(theme: Theme) {
  current = theme;
  if (theme === 'light') {
    document.documentElement.setAttribute('data-theme', 'light');
  } else {
    document.documentElement.removeAttribute('data-theme');
  }
  try {
    localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    /* modo privado: el tema vive solo esta sesión */
  }
  listeners.forEach(fn => fn());
}

// Aplica el tema persistido al cargar el módulo, antes del primer render.
if (typeof document !== 'undefined' && current === 'light') {
  document.documentElement.setAttribute('data-theme', 'light');
}

export function useTheme(): { theme: Theme; toggleTheme: () => void } {
  const theme = useSyncExternalStore(
    cb => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => current,
  );
  const toggleTheme = useCallback(() => {
    apply(current === 'light' ? 'dark' : 'light');
  }, []);
  return { theme, toggleTheme };
}
